import Tooltip from '@/components/UI/Tooltip';
import { formatNumber, isNumericString } from '@/helpers';
import cn from 'classnames';
import type { FC } from 'react';
import type { DataTableCellIndexCoordinate } from '@/components/UI/DataTableV2/DataTable.model';
import styles from '../DataTableV2.module.scss';
import { useDataTable } from '../DataTableProvider';
import { useCellAlerts } from '../hooks/useCellAlerts';
import { useCellCoordinate } from '../hooks/useCellCoordinate';
import { useCellData } from '../hooks/useCellData';
import { useCellEditability } from '../hooks/useCellEditability';
import { useCellFlash } from '../hooks/useCellFlash';
import { useCellPositioning } from '../hooks/useCellPositioning';
import { useCellSelectionState } from '../hooks/useCellSelectionState';
import { useCellState } from '../hooks/useCellState';

const formatCellValue = (value: string): string => (isNumericString(value) ? `${formatNumber(value)}` : value);

interface DataTableDefaultCellProps {
  coordinate: DataTableCellIndexCoordinate;
  positionOffset: { x: number; y: number };
}

/**
 * Pure display cell for text/number/measurement columns.
 *
 * Renders the cell value with formatting but no editing affordances.
 * Editing is handled by the centralized SingleCellEditor (Task 4+).
 */
export const DataTableDefaultCell: FC<DataTableDefaultCellProps> = ({ coordinate, positionOffset }) => {
  const { dataTableService } = useDataTable();
  const { column, row } = coordinate;

  const { idCoordinate, cellRef } = useCellCoordinate(coordinate);
  const { cellData } = useCellData(idCoordinate, cellRef);
  const { cellState, tooltip } = useCellState(idCoordinate, cellRef);
  const { selectionState } = useCellSelectionState(coordinate, idCoordinate);
  const { hasAlert } = useCellAlerts(idCoordinate, cellRef);
  const { isColumnLocked, isColumnReadonly } = useCellEditability(coordinate);
  const flashClass = useCellFlash(idCoordinate);
  const { containerStyle, contentStyle } = useCellPositioning(positionOffset);

  const cellType = dataTableService.columnByIndex(coordinate?.column)?.type;
  const hasGroupBorder = dataTableService.rightBorderType(coordinate.column) === 'group-separator';

  return (
    <div
      className={cn(styles.CellContainer, { [styles.groupBorder]: hasGroupBorder })}
      data-test-component="DataTableDefaultCell"
      data-test-element="container"
      data-test-key={`(${column},${row})`}
      data-cell-coordinate={`${column},${row}`}
      style={containerStyle}
    >
      <Tooltip render={tooltip}>
        <div
          className={cn(
            styles.Cell,
            styles[selectionState],
            styles[cellState],
            {
              [styles.ReadonlyColumn]: isColumnLocked || isColumnReadonly,
            },
            flashClass
          )}
          data-celltype={cellType}
          data-state={cellState}
          data-test-component="DataTableDefaultCell"
          data-test-element="content"
          style={contentStyle}
        >
          {formatCellValue(cellData?.value ?? '')}
        </div>
      </Tooltip>
      {hasAlert && <div className={styles.CellAlert} data-test-element="alert-chip" />}
    </div>
  );
};
